import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Shared axios instance that always sends cookies for auth
export const api = axios.create({ baseURL: API, withCredentials: true });

// Any write action that hits `require_profile_complete` on the backend returns
// 403 { detail: "profile_incomplete" }. Intercept it globally and push the user
// into onboarding rather than letting them stare at a silent failure.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    if (status === 403 && detail === "profile_incomplete") {
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/my/profile")) {
        toast.error("Finish your profile before doing that.");
        window.location.assign("/my/profile");
      }
    }
    return Promise.reject(err);
  }
);

const AuthContext = createContext({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Once we know this browser has an account, flip the CTA copy on future
      // logged-out visits from "Join Oiiister" to "Sign in".
      if (typeof window !== "undefined") {
        try { localStorage.setItem("oiiister_returning", "1"); } catch {}
      }
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } finally {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    // If the URL fragment contains a session_id, AuthCallback handles it —
    // don't race against it.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, refresh, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Trigger Google sign-in via Emergent Auth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function goToLogin(returnTo) {
  const redirectUrl = window.location.origin + (returnTo || "/");
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

// Copy helper: brand-new browsers see "Join Oiiister"; browsers that have ever
// been signed in see "Sign in" (flag lives in localStorage, set on successful
// /auth/me refresh).
export function signInLabel() {
  if (typeof window === "undefined") return "Join Oiiister";
  try {
    return localStorage.getItem("oiiister_returning") ? "Sign in" : "Join Oiiister";
  } catch {
    return "Join Oiiister";
  }
}
